// src/guides/defaultGuides.js
// The code-shipped Host Playbook default pack (spec §5.3 + finished copy in
// ~/.claude/plans/host-playbook-guide-copy.md, owner-approved facts
// 2026-07-07). Pure data — no JSX, no imports — so node tests can validate
// every guide against the registry normalizer.
//
// Layering (mirrors eventDefaults/EventProvider): DEFAULT_GUIDES is neutral
// and must hold true for ANY brand — no dollar rates, package contents,
// emails or venue policies here; those live in BRAND_GUIDE_OVERRIDES (and,
// in Phase B, per-brand Firestore docs that merge on top). An override
// REPLACES the whole guide for that brand, so each override below is the
// complete guide, not a patch.
//
// Copy rules: second person, short sentences, no em-dashes, {tokens} for
// anything config-driven (tokens.js resolves them; unknown token = blank +
// warn). Body markdown-lite: **bold**, "- " bullets, blank-line paragraphs.

export const DEFAULT_GUIDES = [
  // ---- 1. Start Here -------------------------------------------------------
  {
    id: 'welcome-hosting',
    app: 'portal',
    title: 'You bring the night',
    category: 'gettingStarted',
    order: 10,
    status: 'published',
    icon: 'school',
    summary: 'What hosting is, the weekly rhythm, and where everything lives.',
    audienceRoles: [],
    anchors: ['route:/dashboard/default'],
    steps: [
      {
        kicker: 'WELCOME',
        title: 'You bring the night',
        body: 'Hosting at {brandName} is simple at its core. You invite people to {nightDayName} nights, your guests get looked after at the door, and you get rewarded for everyone who shows up. This playbook shows you how the best hosts do it.'
      },
      {
        kicker: 'THE RHYTHM',
        title: 'One week, four beats',
        body: '- Early week: post your story and start inviting.\n- Midweek: collect names and submit your lists.\n- {nightDayName}: be there, sign in, look after your guests.\n- After: check your results and watch your pay land.\n\nDo those four things every week and the rest takes care of itself.'
      },
      {
        kicker: 'WHAT COUNTS',
        title: 'Payers are the scoreboard',
        body: 'A payer is a guest who arrives on your list and pays entry. Almost everything, your pay, your missions, your rank, traces back to one thing: names you submitted walking through the door. More invited, more arrived, more earned.'
      },
      {
        kicker: 'FIND YOUR WAY',
        title: 'Everything lives here',
        body: 'The Dashboard gives you shortcuts to every tool. The menu has the full list. And this playbook has a guide for each one, from promoting to getting paid.',
        cta: { label: 'Browse the playbook', route: '/playbook' }
      }
    ]
  },

  // ---- 2. Promote & Grow ----------------------------------------------------
  {
    id: 'promote-basics',
    app: 'portal',
    title: 'How to promote your night',
    category: 'promote',
    order: 20,
    status: 'published',
    icon: 'campaign',
    summary: 'The weekly promo routine that fills your list.',
    audienceRoles: [],
    anchors: ['route:/content', 'menu:clubastra-content', 'metric:seasonPayers'],
    steps: [
      {
        kicker: 'EVERY WEEK',
        title: 'Post the flyer, every {nightDayName}',
        body: 'Consistency beats creativity. Post the current flyer to your story every week, early enough for people to make plans. Fresh flyers and videos are in Host Promo Content, ready to download.',
        cta: { label: 'Open Promo Content', route: '/content' }
      },
      {
        kicker: 'GO DIRECT',
        title: 'DMs fill lists, stories remind',
        body: 'Stories warm people up. Direct messages get names. Every week, message the people most likely to come and make it personal:\n- "Putting together a table for {nightDayName}, you in?"\n- "I can get you and your girls on my list this week, want in?"\n\nA close-friends story with "reply for the list" also works. Ask for the name right there in the chat.'
      },
      {
        kicker: 'YOUR LINK',
        title: 'One link that tracks your signups',
        body: 'Grab your personal link from the Missions page and put it in your bio and stories. Anyone who signs up through it counts to you, automatically.',
        requiresConfig: 'social.trackedLinks'
      },
      {
        kicker: 'LOCK IT IN',
        title: 'Confirm on the day',
        body: 'People forget. On the day, message everyone who said yes: confirm they are on the list, tell them the cutoff, tell them where to come. A ten second reminder is the difference between a yes and a no-show.',
        cta: { label: "Grab this week's assets", route: '/content' }
      }
    ]
  },

  // ---- 3. Lists & Bookings --------------------------------------------------
  {
    id: 'guestlist-how',
    app: 'portal',
    title: 'Guestlists, free entries and birthdays',
    category: 'bookings',
    order: 30,
    status: 'published',
    icon: 'list',
    summary: 'The three ways to get someone in, and how to submit names right.',
    audienceRoles: [],
    anchors: ['route:/addGuestlist', 'menu:clubastra-addGuestlist', 'route:/freeentry', 'menu:clubastra-freeentry'],
    steps: [
      {
        kicker: 'KNOW THE DIFFERENCE',
        title: 'Three ways to get someone in',
        body: '- **Guestlist**: discounted entry for your guests, valid before {cutoffTime}. Your everyday list, no limit on names.\n- **Free entries**: your personal free spots for the night. No rules on who, they exist so you can look after your friends. You get a set number each night, spend them on the people who matter.\n- **Birthdays**: the birthday offer, its own list with its own perks.'
      },
      {
        kicker: 'GET IT RIGHT',
        title: 'Names the door can find',
        body: 'The door searches exactly what you type. Full first and last name, spelled the way their ID reads. Submit before {cutoffTime}; after that the discount ends and your guest pays full entry.'
      },
      {
        kicker: 'ON THE NIGHT',
        title: 'What your guest does',
        body: 'They give your name or their name at the door, they get marked off, done. If they have trouble, you are their host: be reachable, or better, be at the door.',
        cta: { label: 'Add your first names', route: '/addGuestlist' }
      }
    ]
  },
  {
    id: 'birthdays-sell',
    app: 'portal',
    title: 'Birthdays bring groups',
    category: 'bookings',
    order: 31,
    status: 'published',
    icon: 'cake',
    summary: 'Why birthdays are the highest-value booking, and how to land them.',
    audienceRoles: [],
    anchors: ['route:/bday', 'menu:clubastra-bday', 'metric:birthdayShows'],
    steps: [
      {
        kicker: 'THE BIG ONE',
        title: 'Birthdays bring groups',
        body: 'A birthday is not one guest, it is ten. The birthday person picks the venue and the whole group follows. One booked birthday can be your best night of the month.'
      },
      {
        kicker: 'THE OFFER',
        title: 'What they get',
        body: "Your venue runs a birthday offer for the birthday person and their group. Check the Birthdays page for what's included this season."
      },
      {
        kicker: 'THE PITCH',
        title: 'Ask early, ask everyone',
        body: 'Scan your feed for upcoming birthdays and message them two weeks out:\n- "Your birthday is coming up, let me sort your night. Free entry for the group on my list, just send me the names."\n\nThe earlier you ask, the less chance another venue got there first.'
      },
      {
        kicker: 'BOOK IT',
        title: 'Submit the details',
        body: 'Name of the birthday person, their date, and the guest names. Submit it on the Birthdays page and the door takes care of the rest.',
        cta: { label: 'Book a birthday', route: '/bday' }
      }
    ]
  },
  {
    id: 'booths-vip',
    app: 'portal',
    title: 'Booths and VIP tables',
    category: 'bookings',
    order: 32,
    status: 'published',
    icon: 'liquor',
    summary: 'What a booth is, who to pitch, and how to submit the enquiry.',
    audienceRoles: [],
    requiresFeature: 'booths',
    anchors: ['route:/booths', 'menu:clubastra-booths', 'metric:boothsBooked'],
    steps: [
      {
        kicker: 'VIP',
        title: 'What a booth is',
        body: 'A booth is a reserved VIP table with bottle service: the group gets their own space, their own bottles, and priority entry. It is the highest-value booking you can bring.'
      },
      {
        kicker: 'PACKAGES',
        title: 'Ways to book',
        body: 'Current packages and pricing are on the booking page.'
      },
      {
        kicker: 'SPOT THE LEAD',
        title: 'Who to pitch',
        body: 'Birthdays that outgrew the guestlist. Groups of six or more. Anyone who asks "can we get a table?" Someone celebrating a deal, a break-up or a pay day. When you hear it, offer it: "I can lock you in a booth, want me to set it up?"'
      },
      {
        kicker: 'BOOK IT',
        title: 'Submit the enquiry',
        body: "Put the enquiry through the Booths page with the group's name, package and numbers.",
        cta: { label: 'Submit a booth enquiry', route: '/booths' }
      }
    ]
  },
  {
    id: 'dinners-how',
    app: 'portal',
    title: 'Dinner bookings',
    category: 'bookings',
    order: 33,
    status: 'published',
    icon: 'restaurant',
    summary: 'Start the night at the table.',
    audienceRoles: [],
    requiresFeature: 'dinnerBookings',
    anchors: ['route:/addDinner', 'menu:clubastra-dinner'],
    steps: [
      {
        kicker: 'DINNER FIRST',
        title: 'Dinner before the night',
        body: '{brandName} takes dinner bookings that roll straight into the night. Ask your manager what the current offer is and who it is for.'
      },
      {
        kicker: 'BOOK IT',
        title: 'Submit the booking',
        body: 'Names, numbers and time. Submit it on the Dinner page and your manager will confirm it with you.',
        cta: { label: 'Book a dinner', route: '/addDinner' }
      }
    ]
  },

  // ---- 4. On the Night ------------------------------------------------------
  {
    id: 'night-at-the-door',
    app: 'portal',
    title: 'Your night at the venue',
    category: 'door',
    order: 40,
    status: 'published',
    icon: 'door',
    summary: 'Sign in, work the door, sign out. Why worked nights count.',
    audienceRoles: [],
    anchors: ['route:/dashboard/default', 'metric:signIns'],
    steps: [
      {
        kicker: 'ARRIVE',
        title: 'Sign in when you get there',
        body: 'Your night officially starts when you sign in from your Dashboard.'
      },
      {
        // Photo detail only where the brand actually runs photo sign-in.
        kicker: 'ARRIVE',
        title: 'A quick photo at sign-in',
        body: 'You will be asked for a quick photo when you sign in. It helps the team confirm who worked the night.',
        requiresFeature: 'hostPhotoSignIn'
      },
      {
        kicker: 'WORK THE DOOR',
        title: 'Be findable',
        body: 'Your guests are walking in because of you. Be near the door when your people arrive, help them get marked off, fix spelling problems on the spot. A host at the door converts maybes into payers.'
      },
      {
        kicker: 'WRAP UP',
        title: 'Sign out before you leave',
        body: 'Sign out when you are done. Signed-in-and-out nights are what count as a worked night on your record.'
      },
      {
        kicker: 'BONUS',
        title: 'Sign-ins feed your streak',
        body: 'Worked nights count toward missions and keep your weekly streak alive, which multiplies your XP. Showing up literally pays.',
        requiresConfig: 'missions.enabled',
        cta: { label: 'Go to your Dashboard', route: '/dashboard/default' }
      }
    ]
  },

  // ---- 5. Getting Paid ------------------------------------------------------
  {
    id: 'getting-paid',
    app: 'portal',
    title: 'How your pay works',
    category: 'pay',
    order: 50,
    status: 'published',
    icon: 'atm',
    summary: 'What you earn, where to check it, and how to cash out.',
    audienceRoles: [],
    anchors: ['route:/pays', 'menu:clubastra-pays'],
    steps: [
      {
        kicker: 'THE DEAL',
        title: 'How your pay works',
        body: 'You earn for every paying guest who arrives on one of your lists. Your manager will confirm your rate.'
      },
      {
        kicker: 'CHECK IT',
        title: 'Read your pays page',
        body: 'Host Payments shows every night, who arrived on your lists and what you earned. Check it after every night. If something looks off, raise it with your manager early.'
      },
      {
        kicker: 'YOUR WALLET',
        title: 'Bonuses land in your wallet',
        body: 'Mission cash and bonuses go to your host wallet on the same page. You can cash out once your balance reaches {cashoutMinimum}. First cash-out needs a quick payment form, the page walks you through it.',
        requiresConfig: 'wallet.enabled'
      },
      {
        kicker: 'DONE',
        title: 'Keep it moving',
        body: 'More names in, more payers through the door, bigger pays. Back to the playbook if you want the tactics.',
        cta: { label: 'Open Host Payments', route: '/pays' }
      }
    ]
  },
  {
    id: 'recruit-friends',
    app: 'portal',
    title: 'Great hosts know great hosts',
    category: 'promote',
    order: 21,
    status: 'published',
    icon: 'personAdd',
    summary: 'Grow your nights by bringing in people with their own crowd.',
    audienceRoles: [],
    anchors: ['route:/requestHost', 'menu:clubastra-request-host', 'metric:recruitsActivated', 'metric:activeTeamSize'],
    steps: [
      {
        kicker: 'MULTIPLY',
        title: 'Great hosts know great hosts',
        body: 'The easiest way to grow your nights is to bring in people with their own crowd. Friends who always know where everyone is going, that is the profile.'
      },
      {
        kicker: 'THE BONUS',
        title: 'You both get paid',
        body: 'When someone you recruit gets their first {referralActivationPayers} paying guests, you earn {referralRecruiterBonus} and they earn {referralRecruitBonus}, straight into your wallets.',
        requiresConfig: 'referral.enabled'
      },
      {
        kicker: 'DO IT',
        title: 'Put their name forward',
        body: 'Submit their details on Request New Host. The team reviews it and gets them set up, you just make the introduction.',
        cta: { label: 'Request a new host', route: '/requestHost' }
      }
    ]
  },

  // ---- 6. Missions & Perks --------------------------------------------------
  {
    id: 'missions-xp',
    app: 'portal',
    title: 'Missions, XP and perks',
    category: 'game',
    order: 60,
    status: 'published',
    icon: 'badge',
    summary: 'Real cash and door perks for the hosting you already do.',
    audienceRoles: [],
    requiresConfig: 'missions.enabled',
    anchors: ['route:/missions', 'menu:clubastra-missions'],
    steps: [
      {
        kicker: 'THE GAME',
        title: 'Sixty seconds, worth it',
        body: 'Missions pay real cash for the hosting you already do, and XP unlocks perks at the door. The tour explains it best.',
        cta: { label: 'Play the tour', action: 'welcomeTour' }
      }
    ]
  }
];

// Per-brand code overrides (Phase A stand-in for the Phase B Firestore
// layer). Business facts owner-confirmed 2026-07-07 — see
// ~/.claude/plans/host-playbook-guide-copy.md. Each entry replaces the
// default guide with the same id wholesale.
export const BRAND_GUIDE_OVERRIDES = {
  'club-astra': [
    // Promote guide + the Friday-exclusivity house rule.
    {
      ...DEFAULT_GUIDES.find((g) => g.id === 'promote-basics'),
      steps: [
        ...DEFAULT_GUIDES.find((g) => g.id === 'promote-basics').steps.map((s) =>
          // The final CTA moves to the new closing step below.
          s.kicker === 'LOCK IT IN' ? { ...s, cta: undefined } : s
        ),
        {
          kicker: 'HOUSE RULES',
          title: 'Fridays are Astra nights',
          body: "One rule: Fridays are {brandName} only. Don't host or promote for another venue at the same time. If you're at another venue for personal reasons, a friend's birthday or a night off, that's completely fine, just keep it off your socials.",
          cta: { label: "Grab this week's assets", route: '/content' }
        }
      ]
    },
    // Birthday guide with the real Complimentary Birthday Package.
    {
      ...DEFAULT_GUIDES.find((g) => g.id === 'birthdays-sell'),
      steps: [
        {
          kicker: 'THE BIG ONE',
          title: 'Birthdays bring groups',
          body: 'A birthday is not one guest, it is ten. The birthday person picks the venue and the whole group follows. One booked birthday can be your best night of the month.'
        },
        {
          kicker: 'THE OFFER',
          title: 'The Complimentary Birthday Package',
          body: "Every birthday booked on your list gets:\n- Free VIP entry for the birthday person and 5 guests\n- A $100 drink card when they arrive with 5 or more guests\n- A bottle of champagne and a table when they arrive with 10 or more girls, subject to availability\n\nThat's a real package. Lead with it."
        },
        {
          kicker: 'THE PITCH',
          title: 'Ask early, ask everyone',
          body: 'Scan your feed for upcoming birthdays and message them two weeks out:\n- "Your birthday\'s coming up, let me sort your night. Free VIP entry for you and your group, plus a drink card. Just send me the names."\n\nThe earlier you ask, the less chance another venue got there first.'
        },
        {
          kicker: 'BOOK IT',
          title: 'Book it HERE, not the website',
          body: 'Submit the birthday through this portal, not the public website. Booked here, it attaches to your account, so the night counts to you and you get the recognition and the rewards. Name of the birthday person, the date, the guest names, done.',
          cta: { label: 'Book a birthday', route: '/bday' }
        }
      ]
    },
    // Booth guide with real packages + commission.
    {
      ...DEFAULT_GUIDES.find((g) => g.id === 'booths-vip'),
      steps: [
        {
          kicker: 'VIP',
          title: 'What a booth is, and what it pays YOU',
          body: 'A booth is a reserved VIP table with bottle service: the group gets their own space, their own bottles, and priority entry. It is the highest-value booking you can bring, and you earn commission on every booth you book. Hosts earn 5%. Head Hosts and Ambassadors earn 10%.'
        },
        {
          kicker: 'PACKAGES',
          title: 'Four ways to book',
          body: '- **$550**: Belvedere 700ml (or any premium spirit), WP bottle, 5 VIP priority entries, personalised sign, VIP booth area\n- **$1000**: 2x Belvedere 700ml, WP bottle, 8 VIP priority entries, personalised sign, booth + waitress\n- **$1500**: Belvedere 1.75L, 2x Voir Vodka, 10 VIP priority entries, personalised sign, booth + waitress\n- **$2000**: Belvedere 3L, 2x Voir Vodka, 15 VIP priority entries, personalised sign, booth + waitress',
          media: { type: 'image', src: '/Booth Story.jpeg' }
        },
        {
          kicker: 'SPOT THE LEAD',
          title: 'Who to pitch',
          body: 'Birthdays that outgrew the guestlist. Groups of six or more. Anyone who asks "can we get a table?" Someone celebrating a deal, a break-up or a pay day. When you hear it, offer it: "I can lock you in a booth, want me to set it up?"'
        },
        {
          kicker: 'BOOK IT',
          title: 'Submit the enquiry',
          body: "Put the enquiry through the Booths page with the group's name, package and numbers. Book it through the portal so the commission tracks to you.",
          cta: { label: 'Submit a booth enquiry', route: '/booths' }
        }
      ]
    },
    // Pay guide with the real rates + escalation email. THE DEAL step is
    // role-split (owner 2026-07-08): hosts are on $5/payer; Head Hosts and
    // Ambassadors are on individually agreed weekly retainers, so their step
    // names the structure without promising an amount. Management/admin are
    // in every deal step's audience so they can review all structures.
    {
      ...DEFAULT_GUIDES.find((g) => g.id === 'getting-paid'),
      steps: [
        {
          kicker: 'THE DEAL',
          title: '$5 per payer, no minimums',
          body: 'You earn $5 for every guestlist payer, every guest who arrives on your list and pays entry. No minimums, no thresholds. One payer, $5. Forty payers, $200. It scales with exactly one thing: how many of your people show up.',
          audienceRoles: ['host', 'dj', 'management', 'admin']
        },
        {
          kicker: 'THE DEAL',
          title: 'Head Hosts: your retainer plus your team',
          body: 'As a Head Host you are on a weekly retainer, agreed with management. On top of that you earn booth commission: 10% on booths you book, and 5% on booths your team books. Your numbers and your team’s numbers are what your retainer is built on, so keep both climbing.',
          audienceRoles: ['head_host', 'management', 'admin']
        },
        {
          kicker: 'THE DEAL',
          title: 'Ambassadors: your retainer plus commission',
          body: 'As an Ambassador you are on a weekly retainer, agreed with management. On top of that you earn 10% commission on every booth you book. Your retainer reflects the crowd you bring, so the numbers still matter every week.',
          audienceRoles: ['ambassador', 'management', 'admin']
        },
        {
          kicker: 'CHECK IT',
          title: 'Read your pays page',
          body: 'Host Payments shows every night, who arrived on your lists and what you earned. Check it after every night. If something looks off, raise it with your manager early, or email info@clubastra.com.au.'
        },
        {
          kicker: 'YOUR WALLET',
          title: 'Bonuses land in your wallet',
          body: 'Mission cash and bonuses go to your host wallet on the same page. You can cash out once your balance reaches {cashoutMinimum}. First cash-out needs a quick payment form, the page walks you through it.',
          requiresConfig: 'wallet.enabled'
        },
        {
          kicker: 'DONE',
          title: 'Keep it moving',
          body: 'More names in, more payers through the door, bigger pays. Back to the playbook if you want the tactics.',
          cta: { label: 'Open Host Payments', route: '/pays' }
        }
      ]
    },
    // Dinners are PAUSED at Club Astra (owner 2026-07-07) but the feature
    // flag is still true in eventDefaults — this override ships the honest
    // "benefit for performers" framing until the owner flips the flag or
    // brings dinners back.
    {
      ...DEFAULT_GUIDES.find((g) => g.id === 'dinners-how'),
      steps: [
        {
          kicker: 'A PERK',
          title: 'Dinner before the night',
          body: "Complimentary dinner at the venue is a benefit for performing hosts. When it's running, allocations go to Hosts, Head Hosts and Ambassadors who are putting up numbers. Dinner rolls straight into the night."
        },
        {
          kicker: 'BOOK IT',
          title: 'Submit the booking',
          body: 'If you have an allocation, submit the booking with names, numbers and time, and your manager will confirm it with you.',
          cta: { label: 'Book a dinner', route: '/addDinner' }
        }
      ]
    }
  ]
};
